// officeHoursShape — pure converters between the OfficeHours page's internal
// shape (short day keys 'mon'..'sun', ranges {start,end}) and the canonical
// /api/office-hours document shape (full lowercase day names 'monday'..'sunday',
// ranges {start,end}) that stratus_routing_vars.lua and the API store (#907).
//
// Tolerant on input: accepts either key style and either {start,end} or the
// legacy {from,to} range shape, so a doc written by any past version loads.

export type Range = { start: string; end: string };
export type UiDayRanges = Record<string, Range[]>;

export const UI_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const CANONICAL_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

const UI_TO_CANONICAL: Record<string, string> = Object.fromEntries(
  UI_DAYS.map((d, i) => [d, CANONICAL_DAYS[i]]),
);

function asRange(r: any): Range | null {
  if (!r || typeof r !== 'object') return null;
  const start = r.start ?? r.from;
  const end = r.end ?? r.to;
  if (!start || !end) return null;
  return { start: String(start), end: String(end) };
}

// Canonical (or legacy) day-keyed map → UI map keyed 'mon'..'sun'. Returns
// undefined when the input has no non-empty day at all, so the caller can
// apply its "never configured" 9-5 workday prefill.
export function canonicalToUi(days: any): UiDayRanges | undefined {
  if (!days || typeof days !== 'object') return undefined;
  const out: UiDayRanges = {};
  let any = false;
  for (let i = 0; i < UI_DAYS.length; i++) {
    const ui = UI_DAYS[i];
    const raw = days[CANONICAL_DAYS[i]] ?? days[ui];
    const ranges = Array.isArray(raw) ? (raw.map(asRange).filter(Boolean) as Range[]) : [];
    if (ranges.length) any = true;
    out[ui] = ranges;
  }
  return any ? out : undefined;
}

// UI map keyed 'mon'..'sun' → canonical map keyed 'monday'..'sunday' with all
// seven days present (empty array = closed that day).
export function uiToCanonical(days: UiDayRanges): Record<string, Range[]> {
  const out: Record<string, Range[]> = {};
  for (const ui of UI_DAYS) {
    out[UI_TO_CANONICAL[ui]] = (days[ui] || []).map(asRange).filter(Boolean) as Range[];
  }
  return out;
}
